import React from 'react';
import { AlertCircleIcon, ArrowUpIcon } from 'lucide-react';
import { useEntitlements, getUsagePercent } from '../hooks/useEntitlements';
import { trackUpsellEvent } from '../utils/upsellCopy';
interface UsageMeterProps {
  variant?: 'footer' | 'inline';
  onUpgrade?: () => void;
}
export function UsageMeter({
  variant = 'footer',
  onUpgrade
}: UsageMeterProps) {
  const entitlements = useEntitlements();
  const programsPercent = getUsagePercent(entitlements.usage.programs, entitlements.limits.programs);
  const playsPercent = getUsagePercent(entitlements.usage.plays, entitlements.limits.plays);
  const isNearLimit = programsPercent >= 60 || playsPercent >= 60;
  const isAtLimit = programsPercent >= 100 || playsPercent >= 100;
  // Only show for Free plan and when near limit
  if (entitlements.plan !== 'Free' || !isNearLimit) return null;
  const handleUpgradeClick = () => {
    trackUpsellEvent('upsell_click', {
      surface: 'usage_meter',
      cta: 'upgrade'
    });
    onUpgrade?.();
  };
  if (variant === 'inline') {
    return <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-id="element-3574">
        <div className="flex items-center justify-between mb-2" data-id="element-3575">
          <div className="flex items-center gap-2" data-id="element-3576">
            <AlertCircleIcon className="w-4 h-4 text-orange-600" data-id="element-3577" />
            <span className="text-sm font-medium text-orange-900" data-id="element-3578">
              {isAtLimit ? 'Limit Reached' : 'Approaching Limit'}
            </span>
          </div>
          <button onClick={handleUpgradeClick} className="text-xs font-medium text-orange-600 hover:text-orange-700" data-id="element-3579">
            Upgrade →
          </button>
        </div>
        <div className="space-y-2" data-id="element-3580">
          <div data-id="element-3581">
            <div className="flex items-center justify-between mb-1" data-id="element-3582">
              <span className="text-xs text-gray-600" data-id="element-3583">Programs</span>
              <span className="text-xs font-semibold text-gray-900" data-id="element-3584">
                {entitlements.usage.programs} / {entitlements.limits.programs}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-3585">
              <div className={`h-full rounded-full ${programsPercent >= 100 ? 'bg-red-600' : programsPercent >= 80 ? 'bg-orange-600' : 'bg-blue-600'}`} style={{
              width: `${Math.min(programsPercent, 100)}%`
            }} data-id="element-3586" />
            </div>
          </div>
          <div data-id="element-3587">
            <div className="flex items-center justify-between mb-1" data-id="element-3588">
              <span className="text-xs text-gray-600" data-id="element-3589">Plays</span>
              <span className="text-xs font-semibold text-gray-900" data-id="element-3590">
                {entitlements.usage.plays} / {entitlements.limits.plays}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-3591">
              <div className={`h-full rounded-full ${playsPercent >= 100 ? 'bg-red-600' : playsPercent >= 80 ? 'bg-orange-600' : 'bg-blue-600'}`} style={{
              width: `${Math.min(playsPercent, 100)}%`
            }} data-id="element-3592" />
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-gray-200 px-6 py-3 z-30 shadow-lg" data-id="element-3593">
      <div className="flex items-center justify-between" data-id="element-3594">
        <div className="flex items-center gap-6" data-id="element-3595">
          {/* Programs Usage */}
          <div className="flex items-center gap-3" data-id="element-3596">
            <div className="flex-1" data-id="element-3597">
              <div className="flex items-center justify-between mb-1" data-id="element-3598">
                <span className="text-xs font-medium text-gray-700" data-id="element-3599">
                  Programs
                </span>
                <span className="text-xs font-semibold text-gray-900" data-id="element-3600">
                  {entitlements.usage.programs} / {entitlements.limits.programs}
                </span>
              </div>
              <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-3601">
                <div className={`h-full rounded-full transition-all ${programsPercent >= 100 ? 'bg-red-600' : programsPercent >= 80 ? 'bg-orange-600' : 'bg-blue-600'}`} style={{
                width: `${Math.min(programsPercent, 100)}%`
              }} data-id="element-3602" />
              </div>
            </div>
          </div>
          {/* Plays Usage */}
          <div className="flex items-center gap-3" data-id="element-3603">
            <div className="flex-1" data-id="element-3604">
              <div className="flex items-center justify-between mb-1" data-id="element-3605">
                <span className="text-xs font-medium text-gray-700" data-id="element-3606">Plays</span>
                <span className="text-xs font-semibold text-gray-900" data-id="element-3607">
                  {entitlements.usage.plays} / {entitlements.limits.plays}
                </span>
              </div>
              <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-3608">
                <div className={`h-full rounded-full transition-all ${playsPercent >= 100 ? 'bg-red-600' : playsPercent >= 80 ? 'bg-orange-600' : 'bg-blue-600'}`} style={{
                width: `${Math.min(playsPercent, 100)}%`
              }} data-id="element-3609" />
              </div>
            </div>
          </div>
          {isNearLimit && <div className="flex items-center gap-2 text-orange-600" data-id="element-3610">
              <AlertCircleIcon className="w-4 h-4" data-id="element-3611" />
              <span className="text-xs font-medium" data-id="element-3612">
                {isAtLimit ? 'Limit reached' : 'Approaching plan limits'}
              </span>
            </div>}
        </div>
        <button onClick={handleUpgradeClick} className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all text-sm font-medium" data-id="element-3613">
          <ArrowUpIcon className="w-4 h-4" data-id="element-3614" />
          Upgrade Plan
        </button>
      </div>
    </div>;
}